import { api, unwrap } from "@lumberset/api-client";
import { queryOptions } from "@tanstack/react-query";

/**
 * The reference price book: the book, one item's history, proposals, the
 * count waiting for an Admin and the market signal. Every key starts with
 * "prices", so a change invalidates them all at once.
 */

export const priceCategories = [
  "dimension",
  "studs",
  "treated",
  "timbers",
  "panels",
  "engineered",
  "hardware",
  "other",
] as const;
export type PriceCategory = (typeof priceCategories)[number];

export const categoryLabels: Record<PriceCategory, string> = {
  dimension: "Dimension lumber",
  studs: "Studs",
  treated: "Treated lumber",
  timbers: "Timbers and posts",
  panels: "Panels",
  engineered: "Engineered lumber",
  hardware: "Hardware",
  other: "Other",
};

export const priceSources = [
  "starter_estimate",
  "supplier_quote",
  "import",
  "staff_proposal",
  "admin",
  "market_adjustment",
] as const;
export type PriceSource = (typeof priceSources)[number];

export const sourceLabels: Record<PriceSource, string> = {
  starter_estimate: "Starter estimate",
  supplier_quote: "Supplier quote",
  import: "Price sheet",
  staff_proposal: "Staff research",
  admin: "Admin",
  market_adjustment: "Market move",
};

export const priceUnits = ["ea", "lf", "bf", "mbf", "sheet", "box"] as const;
export type PriceUnit = (typeof priceUnits)[number];

export const unitLabels: Record<PriceUnit, string> = {
  ea: "Each (ea)",
  lf: "Lineal foot (lf)",
  bf: "Board foot (bf)",
  mbf: "Thousand board feet (mbf)",
  sheet: "Sheet",
  box: "Box",
};

export type ProposalStatus = "pending" | "approved" | "rejected" | "withdrawn";

export interface BookFilters {
  region?: string;
  category?: PriceCategory;
  source?: PriceSource;
  q?: string;
  active?: "active" | "inactive" | "all";
}

/** Leaves blank filters out of the request, so "any" is never sent as an empty value. */
function cleanFilters(filters: BookFilters) {
  return {
    region: filters.region?.trim() ? filters.region.trim().toUpperCase() : undefined,
    category: filters.category,
    source: filters.source,
    q: filters.q?.trim() ? filters.q.trim() : undefined,
    active: filters.active ?? "active",
  };
}

export const priceBookQuery = (filters: BookFilters) =>
  queryOptions({
    queryKey: ["prices", "book", cleanFilters(filters)],
    queryFn: () => unwrap(api.staff.prices.$get({ query: cleanFilters(filters) })),
    placeholderData: (previous) => previous,
  });

export type BookPrice = Awaited<
  ReturnType<NonNullable<ReturnType<typeof priceBookQuery>["queryFn"]>>
>["prices"][number];

export const priceHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ["prices", "history", id],
    queryFn: () => unwrap(api.staff.prices[":id"].history.$get({ param: { id } })),
  });

export type PriceHistory = Awaited<
  ReturnType<NonNullable<ReturnType<typeof priceHistoryQuery>["queryFn"]>>
>;

export const proposalsQuery = (filters: { status?: ProposalStatus; mine?: boolean } = {}) =>
  queryOptions({
    queryKey: ["prices", "proposals", filters],
    queryFn: () =>
      unwrap(
        api.staff.prices.proposals.$get({
          query: { status: filters.status, mine: filters.mine ? "1" : undefined },
        }),
      ),
  });

export type Proposal = Awaited<
  ReturnType<NonNullable<ReturnType<typeof proposalsQuery>["queryFn"]>>
>["proposals"][number];

/** Proposals waiting for an Admin: for the sidebar and the Approvals tab. */
export const pendingPricesQuery = queryOptions({
  queryKey: ["prices", "pending-count"],
  queryFn: () => unwrap(api.staff.prices["pending-count"].$get()),
  refetchInterval: 60_000,
});

/** The BLS producer price indexes. The API keeps them for a day, so an hour here is plenty. */
export const marketSignalQuery = queryOptions({
  queryKey: ["prices", "market-signal"],
  queryFn: () => unwrap(api.staff.prices["market-signal"].$get()),
  staleTime: 60 * 60 * 1000,
});

export type MarketSignal = Awaited<ReturnType<NonNullable<(typeof marketSignalQuery)["queryFn"]>>>;

/** How the matcher reads a description, and whether a code is taken, while a form is filled in. */
export const itemCheckQuery = (input: {
  description: string;
  unit: PriceUnit;
  code?: string;
  region?: string;
}) =>
  queryOptions({
    queryKey: ["prices", "check", input],
    queryFn: () =>
      unwrap(
        api.staff.prices.check.$post({
          json: {
            description: input.description,
            unit: input.unit,
            code: input.code?.trim() || undefined,
            region: input.region?.trim() || undefined,
          },
        }),
      ),
    enabled: input.description.trim().length > 0,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
